import React from 'react';
import { CalendarPlus, Navigation, Clock, Play, Square, FileText, CreditCard, Check, Loader2 } from 'lucide-react';

const STEPS = [
  { key: 'appointment', label: 'Appointment', icon: CalendarPlus },
  { key: 'omw', label: 'On My Way', icon: Navigation },
  { key: 'clock', label: 'Start', icon: Play },
  { key: 'invoice', label: 'Invoice', icon: FileText },
  { key: 'payment', label: 'Take Payment', icon: CreditCard },
];

function WorkflowStep({ step, state, onClick, loading }) {
  const isComplete = state === 'complete';
  const isDisabled = state === 'disabled';
  const isAwaiting = state === 'awaiting';
  const isClockedIn = state === 'clocked_in';
  const isAvailable = state === 'default';

  const Icon = isComplete ? Check
    : isClockedIn && step.key === 'clock' ? Square
    : step.icon;
  const label = isClockedIn && step.key === 'clock' ? 'Stop' : step.label;

  let circleClass = 'flex items-center justify-center w-8 h-8 rounded-full transition-all ';
  let labelClass = 'text-[10px] font-semibold uppercase tracking-wide leading-tight text-center transition-colors ';

  if (isComplete) {
    circleClass += 'bg-blue-600 text-white ring-2 ring-blue-200 dark:ring-blue-900';
    labelClass += 'text-blue-700 dark:text-blue-400';
  } else if (isClockedIn) {
    circleClass += 'bg-red-600 text-white ring-2 ring-red-200 dark:ring-red-900 animate-pulse';
    labelClass += 'text-red-700 dark:text-red-400';
  } else if (isDisabled) {
    circleClass += 'bg-gray-50 dark:bg-gray-800/40 text-gray-300 dark:text-gray-600 border border-gray-200 dark:border-gray-700';
    labelClass += 'text-gray-300 dark:text-gray-600';
  } else if (isAwaiting) {
    circleClass += 'border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-350 dark:text-gray-500 group-hover:border-blue-300 group-hover:text-blue-400';
    labelClass += 'text-gray-400 dark:text-gray-500 group-hover:text-blue-400';
  } else {
    // Available — the key state: strong blue presence
    circleClass += 'border-2 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 group-hover:border-blue-600';
    labelClass += 'text-blue-600 dark:text-blue-400 group-hover:text-blue-700';
  }

  return (
    <button
      className={`group flex flex-col items-center gap-1 min-w-[52px] py-1 transition-colors ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      disabled={isDisabled || loading}
      onClick={() => !isDisabled && !loading && onClick?.(step.key)}
      data-testid={`workflow-step-${step.key}`}
      title={label}
    >
      <span className={circleClass}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      </span>
      <span className={labelClass}>{label}</span>
    </button>
  );
}

export default function JobWorkflowTimeline({
  hasAppointment,
  omwRecentlySent,
  omwSending,
  openLogId,
  clockingIn,
  clockingOut,
  hasInvoices,
  hasPaidInvoice,
  hasBalanceDue,
  onAppointment,
  onOmw,
  onClockIn,
  onClockOut,
  onGenerateInvoice,
  onTakePayment,
  hcpMode,
  hasCustomer,
}) {
  if (hcpMode) return null;

  const states = {
    appointment: hasAppointment ? 'complete' : 'default',
    omw: !hasCustomer ? 'disabled' : omwRecentlySent ? 'complete' : 'default',
    clock: openLogId ? 'clocked_in' : 'default',
    invoice: hasInvoices ? 'complete' : 'default',
    payment: hasPaidInvoice ? 'complete' : !hasBalanceDue ? 'awaiting' : 'default',
  };

  const loadingMap = { omw: omwSending, clock: clockingIn || clockingOut };

  const handleClick = (key) => {
    switch (key) {
      case 'appointment': onAppointment(); break;
      case 'omw': onOmw(); break;
      case 'clock': openLogId ? onClockOut() : onClockIn(); break;
      case 'invoice': onGenerateInvoice(); break;
      case 'payment': onTakePayment(); break;
      default: break;
    }
  };

  return (
    <div className="pt-2 border-t border-border" data-testid="job-workflow-timeline">
      <div className="flex items-start gap-1 justify-between sm:justify-start sm:gap-8">
        {STEPS.map((step) => (
          <WorkflowStep
            key={step.key}
            step={step}
            state={states[step.key]}
            onClick={handleClick}
            loading={!!loadingMap[step.key]}
          />
        ))}
      </div>
    </div>
  );
}
